import {useMemo} from 'react';
//mui
import {Box, Tooltip, Typography} from '@mui/material';
//store
import {useAppSelector} from '@/store/hooks';
import {getPrivateTablesAndTicketsTiersSelector, getTablesAndTicketsTiersSelector} from '@/store/organization-tables';
//formik
import {Field, useFormikContext} from 'formik';
import {AddTableTicketFormValues} from '../formConfiguration';
//constants
import {
  AMOUNT,
  BODY1,
  CAPTION,
  COVER_FEE,
  FIELD,
  H5,
  PRICE,
  PRIMARY,
  SUBTITLE1,
  TICKETS_TIER,
  TOP,
  TOTAL,
  YOUR_ORDER,
  COVER_FEE_TEXT,
} from '@/utils/constants';
//types
import {IOrganizationTicketsTiers} from '@/utils/types';
//helpers
import {getCurrentAmount, getTablesSubtotal} from '../TicketsInfo/helpers';
//icons
import {ReactComponent as QuestionIcon} from '@/assets/icons/modal-question.svg';
//components
import Checkbox from '@/shared/components/Checkbox';
import AddTicketActions from '../AddTicketActions';
import PaymentOptions from './PaymentOptions';
//styles
import {paymentTicketsInfoStyle} from './PaymentTicketsInfo.styles';
import {currencyFormatter} from '@/utils/helpers/currency';
import {calculateFees} from '@/utils/helpers/feeCalculations';
import {PAYMENT_METHOD, USER_TYPE} from '@/utils/enums';
import {useApiInstance} from '@/contexts/ApiInstanceContext';
import {getCurrentEventSelector} from '@/store/organization-event';

const PaymentTicketsInfo = ({isFromPage}: {isFromPage?: boolean}) => {
  const {role} = useApiInstance();

  const {
    values: {setTables, isCoverFee, paymentOption},
  } = useFormikContext<AddTableTicketFormValues>();

  const ordinaryTicketsTiers = useAppSelector(getTablesAndTicketsTiersSelector) as IOrganizationTicketsTiers[];
  const privateTicketsTiers = useAppSelector(getPrivateTablesAndTicketsTiersSelector) as IOrganizationTicketsTiers[];
  const currentEvent = useAppSelector(getCurrentEventSelector);

  const ticketsTiers = role === USER_TYPE.USER && privateTicketsTiers ? privateTicketsTiers : ordinaryTicketsTiers;
  // console.log('ticketsTiers', ticketsTiers);

  const getCurrentAmountTables = (tableId: number) => getCurrentAmount(setTables, tableId);
  const getCurrentSubtotalTables = (tableId: number) => getTablesSubtotal(setTables, ticketsTiers, tableId);

  const grossAmount = useMemo(
    () => ticketsTiers.reduce((sum, tier) => sum + getCurrentSubtotalTables(tier.id), 0),
    [setTables, ticketsTiers]
  );

  const canCoverFee = paymentOption === PAYMENT_METHOD.CREDIT_CARD;
  const effectiveIsCoverFee = canCoverFee && isCoverFee;

  const fees = useMemo(
    () =>
      calculateFees(
        +grossAmount,
        effectiveIsCoverFee,
        currentEvent?.organization?.paymentSettings?.transactionFee ?? 0
      ),
    [grossAmount, effectiveIsCoverFee, currentEvent?.organization?.paymentSettings?.transactionFee]
  );

  return (
    <Box sx={paymentTicketsInfoStyle.wrapper}>
      <Box sx={paymentTicketsInfoStyle.content}>
        <Typography variant={SUBTITLE1} sx={paymentTicketsInfoStyle.title}>
          {YOUR_ORDER}
        </Typography>

        <Box sx={paymentTicketsInfoStyle.headerWrapper}>
          <Box sx={paymentTicketsInfoStyle.header}>
            <Typography variant={CAPTION}>{TICKETS_TIER}</Typography>
            <Typography variant={CAPTION}>{AMOUNT}</Typography>
            <Typography variant={CAPTION}>{PRICE}</Typography>
          </Box>

          {ticketsTiers
            // ?.filter((tier) => (user ? tier.isTicketsVisibleOnAuction : true))
            .map((tier) => (
              <Box key={tier.id} sx={paymentTicketsInfoStyle.contentItem}>
                <Typography className="col-name" variant={BODY1}>
                  {tier.name}
                </Typography>
                <Typography className="col-amount" variant={BODY1}>
                  {getCurrentAmountTables(tier.id)}
                </Typography>
                <Typography className="col-total" variant={SUBTITLE1}>
                  {currencyFormatter.format(getCurrentSubtotalTables(tier.id))}
                </Typography>
              </Box>
            ))}

          <Box sx={paymentTicketsInfoStyle.coverFee}>
            <Box sx={paymentTicketsInfoStyle.coverFeeTitle}>
              <Field
                id={FIELD.isCoverFree}
                name={FIELD.isCoverFree}
                value={isCoverFee}
                color={PRIMARY}
                label={COVER_FEE}
                component={Checkbox}
                disabled={!canCoverFee}
              />
              <Tooltip title={`${COVER_FEE_TEXT} info`} placement={TOP}>
                <Box sx={paymentTicketsInfoStyle.coverFeeTooltip}>
                  <QuestionIcon />
                </Box>
              </Tooltip>
            </Box>

            <Typography variant={SUBTITLE1}>
              {effectiveIsCoverFee ? currencyFormatter.format(fees.coverFeeLine) : currencyFormatter.format(0)}
            </Typography>
          </Box>
        </Box>

        <Box sx={paymentTicketsInfoStyle.footer}>
          <Typography variant={H5}>{TOTAL}</Typography>
          <Typography variant={H5}>{currencyFormatter.format(fees.amountChargedToPatron)}</Typography>
        </Box>
      </Box>

      <PaymentOptions />

      <AddTicketActions isFromPage={isFromPage} />
    </Box>
  );
};

export default PaymentTicketsInfo;
